import { config } from '../../config/env'
import { basicAuthHeader, readPosCredential } from '../../config/credentials'
import type { V2CustomerResult } from './types'

/** The V2 POS surface client (DEV-378, spec §13.3).
 *
 * One shape for every call: `{op, schema_version, idempotency_key?, payload}` posted to
 * the surface, answered by either `data` or `error` — never both, by contract.
 *
 * AUTHENTICATION is deliberately not this module's job in the normal case. K-Series
 * authenticates the page load with HTTP Basic, and a web view replays those credentials
 * on same-origin requests by itself; `credentials: 'include'` is what asks for that.
 * An explicit credential is only attached when one has been configured, which covers
 * local dev and any deployment where the surface is not same-origin.
 */

export const SCHEMA_VERSION = 2

export interface V2Error {
  code: string
  message: string
  error_id?: string
  retryable?: boolean
  details?: Record<string, unknown>
  case_id?: string
}

/** A typed refusal from the surface. Carries the §13.8 code, which is what the UI
 * renders from — never this message, which is for operators. */
export class PointsError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly retryable = false,
    readonly details?: Record<string, unknown>,
  ) {
    super(message)
    this.name = 'PointsError'
  }
}

/** The transport failed before any envelope existed — offline, DNS, CORS.
 *
 * Distinct from `PointsError` because the response is different: a typed refusal has
 * words for the cashier, whereas this one means "we could not ask", and §10.6 says the
 * client shows POINTS_SYSTEM_UNAVAILABLE for it.
 */
export class TransportError extends Error {
  readonly code = 'POINTS_SYSTEM_UNAVAILABLE'
}

interface Envelope<T> {
  schema_version: number
  request_id?: string
  server_time?: string
  versions?: Record<string, number>
  data?: T
  error?: V2Error
}

export async function callPos<T>(
  op: string,
  payload: Record<string, unknown> = {},
  options: { idempotencyKey?: string } = {},
): Promise<T> {
  const credential = readPosCredential()

  let response: Response
  try {
    response = await fetch(config.posSurfaceUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(credential ? { Authorization: basicAuthHeader(credential) } : {}),
      },
      // Same-origin: replays the Basic credentials K-Series authenticated the page with.
      credentials: 'include',
      body: JSON.stringify({
        op,
        schema_version: SCHEMA_VERSION,
        ...(options.idempotencyKey ? { idempotency_key: options.idempotencyKey } : {}),
        payload,
      }),
    })
  } catch (error) {
    throw new TransportError(error instanceof Error ? error.message : String(error))
  }

  // 401 arrives without an envelope: the surface refuses before it parses anything.
  if (response.status === 401) {
    throw new PointsError(
      'POS_CREDENTIAL_REJECTED',
      'this till is not authenticated for that location',
    )
  }

  // HTML from an API path means a rewrite did not match and the SPA catch-all answered.
  // It is worth detecting by name: it looks exactly like an outage to the cashier while
  // being a one-line hosting fix, and "unreachable" would send someone to check the
  // network instead of the config.
  const contentType = response.headers?.get?.('content-type') ?? ''
  if (contentType.includes('text/html')) {
    throw new TransportError(
      `the POS surface returned HTML, not JSON (HTTP ${response.status}). ` +
        `A hosting rewrite is missing, so the SPA answered instead of the API.`,
    )
  }

  let envelope: Envelope<T>
  try {
    envelope = (await response.json()) as Envelope<T>
  } catch {
    // A body we cannot parse is not a refusal we can explain. Treated as transport so
    // the cashier sees "unreachable" rather than a code that means nothing.
    throw new TransportError(`unreadable response (HTTP ${response.status})`)
  }

  if (envelope.error) {
    throw new PointsError(
      envelope.error.code,
      envelope.error.message,
      envelope.error.retryable,
      envelope.error.details,
    )
  }
  if (envelope.data === undefined) {
    throw new TransportError('response carried neither data nor error')
  }
  return envelope.data
}

export interface ValidateResult {
  location_id: number
  permits_value_actions: boolean
  merchant_warning?: string | null
  merchant_error?: string | null
  details?: Record<string, unknown>
}

/** The boot probe. Cheap, allowed while the location is unavailable, and the only call
 * that has to succeed before the till can show anything. */
export function validatePos(): Promise<ValidateResult> {
  return callPos<ValidateResult>('validate', {})
}


/** The scanned customer's wallet and catalogue at this location.
 *
 * `consumer_ref` is whatever the till scanned -- the surface resolves it to a uid
 * itself, so the client never has to know which of the several accepted forms it got.
 */
export function fetchPosCustomer(
  consumerRef: string,
  locationId?: number,
): Promise<V2CustomerResult> {
  return callPos<V2CustomerResult>('customer', {
    consumer_ref: consumerRef,
    ...(locationId != null ? { location_id: locationId } : {}),
  })
}
